import { EditAnalysisFeeCalculatorReturnType } from "@hooks/useFeeCalculator";
import {
  AnalysisFeeCalculatorDTO,
  AnalysisFeePortfolioSetupDTO,
  FamilyMemberDTO,
  FeeCalculatorAutoCalculateConfig,
  FeeCalculatorCalculatedTotals,
  FeeCalculatorDTO,
  FeeCalculatorRenderConditions,
  FeeCalculatorValidStateConditions,
} from "@models/platform-analysis/entities/steps/fee";
import { AnalysisCalculateTotalTypeVariable } from "@models/platform-analysis/enums/fee/feeCalculatorTotalType";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { isEmpty, round, TemplateExecutor, uniq } from "lodash";
import { FieldPath, UseFormGetValues } from "react-hook-form";
import { AnalysisFeeCalculatorStepConfigs } from "./configs";

export const normalizeFeeCalculatorFormData = ({
  originalData,
  resetHasReCalculateData = false,
  removeUnassignedPortfolios = true,
}: {
  originalData: AnalysisFeeCalculatorDTO;
  resetHasReCalculateData?: boolean;
  removeUnassignedPortfolios?: boolean;
}) => {
  const normalizedData: AnalysisFeeCalculatorDTO =
    structuredClone(originalData);
  const portfolioLevelValue = getInitialPortfolioLevel(
    normalizedData.feeCalculators || [],
  );

  const currentFamilyMembers: FamilyMemberDTO[] =
    normalizedData.familyMembers
      ?.filter((member) => member.totalBalance > 0)
      ?.map((member) => ({
        ...member,
        familyMemberAccounts: member.familyMemberAccounts.filter(
          (account) => account.balance > 0,
        ),
      })) || [];

  if (isEmpty(currentFamilyMembers)) {
    const initMember = new FamilyMemberDTO();
    initMember.order = 1;
    initMember.name = "Family member/entity 1";
    currentFamilyMembers.push(initMember);
  }

  normalizedData.familyMembers = currentFamilyMembers;

  const currentPortfolioSetups = normalizedData.portfolioSetups || [];
  for (const setup of currentPortfolioSetups) {
    setup.selectedPortfolioId = setup.selectedPortfolioId ?? null;
  }

  if (removeUnassignedPortfolios) {
    const portfolioIdsInUse =
      portfolioLevelValue === FeeCalculatorPortfolioLevel.No
        ? []
        : normalizedData.portfolioSetups
            ?.map((setup) => setup.selectedPortfolioId)
            ?.filter(Boolean) ?? [];
    normalizedData.portfolios =
      normalizedData.portfolios?.filter((portfolio) =>
        portfolioIdsInUse.includes(portfolio.id),
      ) ?? [];
  }

  if (resetHasReCalculateData) normalizedData.hasRecalculate = false;

  return normalizedData;
};

export const evaluateRenderConditions = ({
  values,
  conditions,
  invalidIfEmpty = false,
}: {
  values: EditAnalysisFeeCalculatorReturnType;
  conditions?: FeeCalculatorRenderConditions;
  invalidIfEmpty?: boolean;
}): boolean => {
  if (!conditions) return !invalidIfEmpty;
  if (typeof conditions === "string") {
    return !!values[conditions];
  }
  if ("and" in conditions) {
    return conditions.and.every((cond) =>
      evaluateRenderConditions({ conditions: cond, values }),
    );
  }
  if ("or" in conditions) {
    return conditions.or.some((cond) =>
      evaluateRenderConditions({ conditions: cond, values }),
    );
  }
  return false;
};

export const getCalculateTotals = (
  familyMembers?: FamilyMemberDTO[],
): FeeCalculatorCalculatedTotals => {
  const initialValue = {
    account: 0,
    total: 0,
    idps: 0,
    super: 0,
  };

  if (!familyMembers) return initialValue;

  familyMembers.forEach((member) => {
    initialValue.account += member.familyMemberAccounts.length;

    member.familyMemberAccounts.forEach((account) => {
      const { isIDPS, balance } = account;
      const numericBalance = Number(balance) || 0;
      if (isIDPS) {
        initialValue.idps += numericBalance;
      } else {
        initialValue.super += numericBalance;
      }
      initialValue.total += numericBalance;
    });
  });

  return initialValue;
};

export const isFeeCalculatorTotalType = (
  item: unknown,
): item is AnalysisCalculateTotalTypeVariable => {
  return Object.values(AnalysisCalculateTotalTypeVariable).includes(
    item as AnalysisCalculateTotalTypeVariable,
  );
};

export const extractAutoCalculateVariablesFromConfig = (
  autoCalculateConfig?: FeeCalculatorAutoCalculateConfig,
): {
  sum: AnalysisVariable[];
  subtract: AnalysisVariable[];
} => {
  const sumVars: AnalysisVariable[] = [];
  const subtractVars: AnalysisVariable[] = [];

  if (!autoCalculateConfig) {
    return { sum: sumVars, subtract: subtractVars };
  }

  if ("sum" in autoCalculateConfig) {
    autoCalculateConfig.sum.forEach((item) => {
      if (isFeeCalculatorTotalType(item)) return;
      sumVars.push(item);
    });
  }

  if ("subtract" in autoCalculateConfig) {
    autoCalculateConfig.subtract.forEach((item) => {
      if (isFeeCalculatorTotalType(item)) return;
      subtractVars.push(item);
    });
  }

  return { sum: sumVars, subtract: subtractVars };
};

export const extractAllVariablesFromEvaluateStateConditions = (
  conditions?: FeeCalculatorValidStateConditions[],
): AnalysisVariable[] => {
  const variables: AnalysisVariable[] = [];

  if (!conditions) return variables;

  conditions.forEach((condition) => {
    const { leftHandComparison, rightHandComparison } = condition;
    const leftVariables =
      extractAutoCalculateVariablesFromConfig(leftHandComparison);
    const rightVariables =
      typeof rightHandComparison === "number"
        ? { sum: [], subtract: [] }
        : extractAutoCalculateVariablesFromConfig(rightHandComparison);

    variables.push(
      ...leftVariables.sum,
      ...leftVariables.subtract,
      ...rightVariables.sum,
      ...rightVariables.subtract,
    );
  });

  return uniq(variables);
};

export const calculateFeeCalculatorComparisonValue = ({
  config,
  totals,
  getValues,
  pathsMap,
}: {
  config: FeeCalculatorAutoCalculateConfig | number;
  totals: FeeCalculatorCalculatedTotals;
  getValues: UseFormGetValues<AnalysisFeeCalculatorDTO>;
  pathsMap: Partial<
    Record<
      AnalysisVariable,
      {
        feeCalculatorPath: FieldPath<AnalysisFeeCalculatorDTO>;
        dataValuePath: FieldPath<AnalysisFeeCalculatorDTO>;
      }
    >
  >;
}): number => {
  let value = typeof config !== "number" ? 0 : config;

  const process = (
    items: (AnalysisVariable | AnalysisCalculateTotalTypeVariable)[],
    sign: 1 | -1,
  ) => {
    let result = 0;
    items.forEach((item) => {
      if (item === AnalysisCalculateTotalTypeVariable.TotalBalance) {
        result += sign * totals.total;
        return;
      }

      if (item === AnalysisCalculateTotalTypeVariable.IDPSTotal) {
        result += sign * totals.idps;
        return;
      }

      if (item === AnalysisCalculateTotalTypeVariable.SuperTotal) {
        result += sign * totals.super;
        return;
      }

      if (item === AnalysisCalculateTotalTypeVariable.TotalAccounts) {
        result += sign * totals.account;
        return;
      }

      const fieldValue = getValues(
        pathsMap[item]?.dataValuePath as FieldPath<AnalysisFeeCalculatorDTO>,
      );
      const numericValue = Number(fieldValue);
      if (!isNaN(numericValue)) result += sign * numericValue;
    });

    return result;
  };

  if (typeof config !== "number" && "sum" in config) {
    value += process(config.sum, 1);
  }

  if (typeof config !== "number" && "subtract" in config) {
    value += process(config.subtract, -1);
  }

  return round(value, AnalysisFeeCalculatorStepConfigs.DefaultDecimalPlaces);
};

export const compileErrorTemplateMessage = (
  messageTemplate?: {
    executor?: TemplateExecutor;
    plainText?: string;
    variables?: AnalysisVariable[];
  },
  relatedFieldName?: Partial<Record<AnalysisVariable, string>>,
): string => {
  if (!messageTemplate) return "";
  if (isEmpty(messageTemplate.variables)) {
    return messageTemplate.plainText || "";
  }

  if (!messageTemplate.executor) return "";
  const funcObject: Record<string, string> = {};
  messageTemplate.variables?.forEach((variable) => {
    const name = AnalysisVariable[variable as keyof typeof AnalysisVariable];
    funcObject[name] = relatedFieldName?.[variable] || "";
  });

  return messageTemplate.executor(funcObject);
};

export const checkValidPortfolioSetupSection = (
  portfolioSetups: AnalysisFeePortfolioSetupDTO[],
) => {
  const errorKeys: (keyof AnalysisFeePortfolioSetupDTO)[] = [
    "isMissingInvestment",
    "isMissingInvestmentOption",
    "hasNoInvestmentData",
    "hasAllocationError",
    "hasUnavailableInvestmentOption",
    "hasUnavailableInvestment",
    "hasCashNotAvailableInSuperError",
    "hasCashNotAvailableInIDPSError",
  ];

  return !portfolioSetups.some((setup) => {
    const {
      excludedFromFeeAnalysis,
      selectedPortfolioId,
      hasNoInvestmentData,
    } = setup;
    if (excludedFromFeeAnalysis || hasNoInvestmentData) return false;
    if (!selectedPortfolioId) return true;

    for (const key of errorKeys) {
      if (setup[key]) return true;
    }
    return false;
  });
};

// #region Private Methods
const getInitialPortfolioLevel = (
  feeCalculators: FeeCalculatorDTO[],
): FeeCalculatorPortfolioLevel => {
  const portfolioSetupCalculatorValue = feeCalculators.find(
    (calc) => calc.variableName === AnalysisVariable.Portfolio_setup,
  )?.feeCalculatorData?.value;
  if (!portfolioSetupCalculatorValue) return FeeCalculatorPortfolioLevel.No;
  return portfolioSetupCalculatorValue as FeeCalculatorPortfolioLevel;
};
// #endregion Private Methods
